import { Box, Button, IconButton, Pagination, Stack, Tab, Tabs, Tooltip } from '@mui/material';
import { Container } from 'Components/Container';
import {
  MenuNotificationBody,
  MenuNotificationContent,
  MenuNotificationIcon,
  MenuNotificationItem,
  MenuNotificationTime,
  MenuNotificationTitle,
} from 'Components/Header/components/MenuNotifications/style';
import { TabPanel } from 'Components/TabPanel';
import { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import {
  getUserNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  archiveNotification,
} from 'services/user-notifications-service';
import { useAuthTenant, useAuthUser } from 'store/auth';
import InboxIcon from '@mui/icons-material/Inbox';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ArchiveIcon from '@mui/icons-material/Archive';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { formatDateTimeToDateTimeBR } from 'utils/date_utils';
import { useTranslation } from 'react-i18next';


const NotificationList = ({ notifications, isLoading, isError, page, totalPages, onPageChange, onMarkAsRead, onArchive, showActions = true }) => {
  const { t } = useTranslation(['user', 'common']);
  if (isLoading) {
    return <Box sx={{ padding: '16px', textAlign: 'center' }}>{t('common:labels.loading')}</Box>;
  }

  if (isError) {
    return <Box sx={{ padding: '16px', textAlign: 'center', color: 'error.main' }}>{t('user:notifications.error')}</Box>;
  }

  if (!notifications || notifications.length === 0) {
    return <Box sx={{ padding: '16px', textAlign: 'center', color: 'text.secondary' }}>{t('user:notifications.empty')}</Box>;
  }

  return (
    <>
      {notifications.map((itemNotification, key) => (
        <MenuNotificationItem key={itemNotification._id || key} sx={{ position: 'relative' }}>
          <MenuNotificationIcon>
            <InboxIcon />
          </MenuNotificationIcon>
          <MenuNotificationBody>
            <MenuNotificationTitle>{itemNotification.titleNotification}</MenuNotificationTitle>
            <MenuNotificationContent>{itemNotification.bodyNotification}</MenuNotificationContent>
            <MenuNotificationTime>
              {formatDateTimeToDateTimeBR(itemNotification.createdDate, true)}
            </MenuNotificationTime>
          </MenuNotificationBody>
          {showActions && (
            <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center', marginLeft: 'auto' }}>
              {onMarkAsRead && (
                <Tooltip title={t('user:notifications.markAsRead')}>
                  <IconButton size="small" onClick={() => onMarkAsRead(itemNotification._id)}>
                    <MarkEmailReadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {onArchive && (
                <Tooltip title={t('common:tooltips.archive')}>
                  <IconButton size="small" onClick={() => onArchive(itemNotification._id)}>
                    <ArchiveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </MenuNotificationItem>
      ))}
      {totalPages > 1 && (
        <Stack sx={{ justifyContent: 'center', flexDirection: 'row', marginTop: '16px' }}>
          <Pagination
            count={totalPages}
            onChange={(_, selectedPage) => onPageChange(selectedPage)}
            color="primary"
            page={page}
            showFirstButton
            showLastButton
          />
        </Stack>
      )}
    </>
  );
};

export const UserNotifications = () => {
  const { t } = useTranslation(['user', 'common']);
  const user = useSelector(useAuthUser);
  const selectedTenant = useSelector(useAuthTenant);
  const queryClient = useQueryClient();
  const [tabPosition, setTabPosition] = useState(0);
  const [pageUnread, setPageUnread] = useState(1);
  const [pageRead, setPageRead] = useState(1);
  const [pageArchived, setPageArchived] = useState(1);

  const {
    data: unreadNotifications,
    isLoading: isLoadingUnread,
    isError: isErrorUnread,
  } = useQuery(['getNotifications', 'unread', user?.userId, selectedTenant?.slug, pageUnread], async () => {
    if (!selectedTenant?.slug) return { notifications: [], page: 1, totalPages: 0 };

    const result = await getUserNotifications(user.userId, selectedTenant.slug, pageUnread, 15, '', 'unread');

    if (result.status === 200) {
      return {
        notifications: result.data.data,
        page: result.data.page,
        totalPages: result.data.totalPages,
      };
    }
    return { notifications: [], page: 1, totalPages: 0 };
  });

  const {
    data: readNotifications,
    isLoading: isLoadingRead,
    isError: isErrorRead,
  } = useQuery(['getNotifications', 'read', user?.userId, selectedTenant?.slug, pageRead], async () => {
    if (!selectedTenant?.slug) return { notifications: [], page: 1, totalPages: 0 };

    const result = await getUserNotifications(user.userId, selectedTenant.slug, pageRead, 15, '', 'read');

    if (result.status === 200) {
      return {
        notifications: result.data.data,
        page: result.data.page,
        totalPages: result.data.totalPages,
      };
    }
    return { notifications: [], page: 1, totalPages: 0 };
  }, { enabled: tabPosition === 1 });

  const {
    data: archivedNotifications,
    isLoading: isLoadingArchived,
    isError: isErrorArchived,
  } = useQuery(['getNotifications', 'archived', user?.userId, selectedTenant?.slug, pageArchived], async () => {
    if (!selectedTenant?.slug) return { notifications: [], page: 1, totalPages: 0 };

    const result = await getUserNotifications(user.userId, selectedTenant.slug, pageArchived, 15, '', 'archived');

    if (result.status === 200) {
      return {
        notifications: result.data.data,
        page: result.data.page,
        totalPages: result.data.totalPages,
      };
    }
    return { notifications: [], page: 1, totalPages: 0 };
  }, { enabled: tabPosition === 2 });

  const invalidateQueries = () => {
    queryClient.invalidateQueries(['getNotifications']);
    queryClient.invalidateQueries(['getTotalCountUserNotification']);
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId, user.userId);
      invalidateQueries();
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead(user.userId, selectedTenant.slug);
      invalidateQueries();
    } catch (error) {
      console.error('Erro ao marcar todas as notificações como lidas:', error);
    }
  };

  const handleArchive = async (notificationId) => {
    try {
      await archiveNotification(notificationId, user.userId);
      invalidateQueries();
    } catch (error) {
      console.error('Erro ao arquivar notificação:', error);
    }
  };

  const handleTabChange = (_, newValue) => {
    setTabPosition(newValue);
  };

  return (
    <Container title={t('user:notifications.title')} linkPage="/user-notifications">
      <Box sx={{ width: '100%' }} className="container">
        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Tabs
            value={tabPosition}
            onChange={handleTabChange}
            aria-label={t('user:notifications.tabs.ariaLabel')}
          >
            <Tab
              label={t('user:notifications.tabs.unread')}
              id="tab-news"
              aria-controls="tabpanel-news"
              style={{ marginLeft: 0, marginTop: 0, width: 'fit-content' }}
            />
            <Tab
              label={t('user:notifications.tabs.read')}
              id="tab-readed"
              aria-controls="tabpanel-readed"
              style={{ marginLeft: 0, marginTop: 0, width: 'fit-content' }}
            />
            <Tab
              label={t('user:notifications.tabs.archived')}
              id="tab-archived"
              aria-controls="tabpanel-archived"
              style={{ marginLeft: 0, marginTop: 0, width: 'fit-content' }}
            />
          </Tabs>
          {tabPosition === 0 && unreadNotifications?.notifications?.length > 0 && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<DoneAllIcon />}
              onClick={handleMarkAllAsRead}
            >
              {t('user:notifications.markAllAsRead')}
            </Button>
          )}
        </Box>
        <TabPanel padding={'8px 0px'} value={tabPosition} index={0} id="tabpanel-news" ariaLabel="tab-news">
          <NotificationList
            notifications={unreadNotifications?.notifications}
            isLoading={isLoadingUnread}
            isError={isErrorUnread}
            page={unreadNotifications?.page || 1}
            totalPages={unreadNotifications?.totalPages || 0}
            onPageChange={setPageUnread}
            onMarkAsRead={handleMarkAsRead}
            onArchive={handleArchive}
          />
        </TabPanel>
        <TabPanel padding={'8px 0px'} value={tabPosition} index={1} id="tabpanel-readed" ariaLabel="tab-readed">
          <NotificationList
            notifications={readNotifications?.notifications}
            isLoading={isLoadingRead}
            isError={isErrorRead}
            page={readNotifications?.page || 1}
            totalPages={readNotifications?.totalPages || 0}
            onPageChange={setPageRead}
            onArchive={handleArchive}
            showActions={true}
            onMarkAsRead={null}
          />
        </TabPanel>
        <TabPanel padding={'8px 0px'} value={tabPosition} index={2} id="tabpanel-archived" ariaLabel="tab-archived">
          <NotificationList
            notifications={archivedNotifications?.notifications}
            isLoading={isLoadingArchived}
            isError={isErrorArchived}
            page={archivedNotifications?.page || 1}
            totalPages={archivedNotifications?.totalPages || 0}
            onPageChange={setPageArchived}
            showActions={false}
          />
        </TabPanel>
      </Box>
    </Container>
  );
};
